import React, { useEffect, useState } from "react";

/**
 * Shared full-screen game launcher (RN ProviderGamesScreen modal parity):
 * dark #0c0d0e takeover, header with uppercase game name + circular ✕,
 * "Loading {name}…" overlay while the game URL is fetched and while the
 * iframe itself loads.
 *
 * Props:
 *  - url: string|null      game URL; when set the full-screen modal shows
 *  - name: string          game display name
 *  - fetching: bool        true while the launch URL is being requested
 *  - onClose: () => void   close/back handler
 */
function GameLaunchModal({ url, name, fetching, onClose }) {
  const [frameLoading, setFrameLoading] = useState(true);

  useEffect(() => {
    if (url) setFrameLoading(true);
  }, [url]);

  // lock body scroll while open
  useEffect(() => {
    const open = !!url || fetching;
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [url, fetching]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && (url || fetching)) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [url, fetching, onClose]);

  if (!url && !fetching) return null;

  // Fetching-URL overlay (before we have a URL)
  if (!url) {
    return (
      <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-center gap-4 bg-[rgba(0,0,0,0.82)]">
        <button
          onClick={onClose}
          aria-label="Cancel"
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-[#1f2937] text-base font-bold text-white"
        >
          ✕
        </button>
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[var(--bet-secondary)] border-t-transparent" />
        <div className="text-base font-semibold text-white">
          Loading {name || "game"}…
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex flex-col bg-[#0c0d0e]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#1f2937] px-4 py-3">
        <span className="mr-3 flex-1 truncate text-base font-bold uppercase tracking-wide text-white">
          {name || "Game"}
        </span>
        <button
          onClick={onClose}
          aria-label="Close game"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1f2937] text-base font-bold text-white"
        >
          ✕
        </button>
      </div>

      {/* Game iframe */}
      <div className="relative flex-1">
        <iframe
          src={url}
          title={name || "Game"}
          className="h-full w-full border-0 bg-[#0c0d0e]"
          allow="autoplay; fullscreen"
          allowFullScreen
          onLoad={() => setFrameLoading(false)}
        />
        {frameLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#0c0d0e]">
            <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-[var(--bet-secondary)] border-t-transparent" />
            <div className="text-base font-semibold text-white">
              {name || "Loading…"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GameLaunchModal;
